/**
 * Persist pasted inline images we actually own bytes for (`data:` and
 * same-origin `blob:` previews) and rewrite src to an internal pointer.
 *
 * Remote `https://` logos (GitLab, vendor CDNs) stay as display URLs — browser
 * `fetch()` of those hosts is CORS-blocked, and treating that as failure used
 * to **delete** the `<img>` after paste. Outlook-on-the-web `blob:https://outlook.office.com/…`
 * URLs are another origin's blob store; they are not rehostable here.
 *
 * Persistence is injected: this module reads and validates bytes, the caller's
 * resolver decides where they go. No request id or online state here.
 */
import { rememberPendingInlineImagePreview } from '../../services/pendingInlineImagePreview';
import {
  MAX_UPLOAD_SIZE_BYTES,
  validateFileForSecurity,
} from '../../utils/fileSecurityValidation';

import type { InlineImageResolution } from '../textArea/inlineImage.types';
import type { Editor } from '@tiptap/core';

const OWNED_SRC = /^sitescope-attachment(-pending)?:\/\//i;
const IMAGE_CAP_BYTES = MAX_UPLOAD_SIZE_BYTES;

/** Bytes we can read from this document. Not `https:` and not another origin's blobs. */
export function isRehostableImageSrc(src: string): boolean {
  const trimmed = src.trim();
  if (/^data:image\//i.test(trimmed)) {
    return true;
  }
  if (!/^blob:/i.test(trimmed)) {
    return false;
  }
  const origin =
    typeof window !== 'undefined' ? window.location?.origin ?? '' : '';
  if (origin.length < 8) {
    return false;
  }
  return trimmed.startsWith(`blob:${origin}`);
}

async function srcToFile(src: string, index: number): Promise<File | null> {
  try {
    const response = await fetch(src);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    if (!blob.type.startsWith('image/') || blob.size <= 0) {
      return null;
    }
    const extension = blob.type.split('/')[1]?.replace('jpeg', 'jpg') ?? 'png';
    return new File([blob], `pasted-inline-${index}.${extension}`, {
      type: blob.type,
    });
  } catch {
    return null;
  }
}

/** Swap a live editor image `src` (blob preview → persisted attachment ref). */
export function replaceEditorImageSrc(
  editor: Editor,
  fromSrc: string,
  toSrc: string,
): boolean {
  const foundPos = findEditorImagePos(editor, fromSrc);
  if (foundPos == null) {
    return false;
  }
  editor
    .chain()
    .setNodeSelection(foundPos)
    .updateAttributes('image', { src: toSrc })
    .run();
  return true;
}

export function deleteEditorImageBySrc(editor: Editor, src: string): boolean {
  const foundPos = findEditorImagePos(editor, src);
  if (foundPos == null) {
    return false;
  }
  editor.chain().setNodeSelection(foundPos).deleteSelection().run();
  return true;
}

function findEditorImagePos(editor: Editor, src: string): number | null {
  let foundPos: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (foundPos != null) {
      return false;
    }
    if (
      node.type.name === 'image' &&
      String(node.attrs.src ?? '').trim() === src
    ) {
      foundPos = pos;
    }
    return true;
  });
  return foundPos;
}

/**
 * Walk the TipTap document and replace non-owned image src values with the
 * references the resolver hands back.
 */
export async function rehostInlineImagesInEditor(
  editor: Editor,
  resolveImage: (file: File) => Promise<InlineImageResolution>,
  inFlight: Set<string>,
): Promise<{ replaced: number; failed: number }> {
  const targets: Array<{ pos: number; src: string; alt: string | null }> = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'image') {
      return;
    }
    const src = String(node.attrs.src ?? '').trim();
    if (!src || OWNED_SRC.test(src) || !isRehostableImageSrc(src)) {
      return;
    }
    if (inFlight.has(src)) {
      return;
    }
    targets.push({ pos, src, alt: node.attrs.alt ?? null });
  });
  targets.sort((left, right) => right.pos - left.pos);

  let replaced = 0;
  let failed = 0;
  for (const [index, target] of targets.entries()) {
    inFlight.add(target.src);
    try {
      const file = await srcToFile(target.src, index);
      if (!file || file.size > IMAGE_CAP_BYTES) {
        editor.chain().setNodeSelection(target.pos).deleteSelection().run();
        failed += 1;
        continue;
      }
      const security = validateFileForSecurity(file);
      if (!security.valid) {
        editor.chain().setNodeSelection(target.pos).deleteSelection().run();
        failed += 1;
        continue;
      }
      const resolution = await resolveImage(file);
      if (!resolution.ok) {
        editor.chain().setNodeSelection(target.pos).deleteSelection().run();
        failed += 1;
        continue;
      }
      // Preserve the already-rendering source while the document stores the
      // durable attachment reference. The image node view resolves that ref
      // through this cache instead of asking the browser to load the custom
      // `sitescope-attachment://` scheme.
      rememberPendingInlineImagePreview(resolution.reference, target.src);
      editor
        .chain()
        .setNodeSelection(target.pos)
        .updateAttributes('image', {
          src: resolution.reference,
          alt: target.alt ?? 'attachment',
        })
        .run();
      replaced += 1;
    } finally {
      inFlight.delete(target.src);
    }
  }

  return { replaced, failed };
}

export function editorHtmlHasRehostableImages(html: string): boolean {
  const srcPattern = /<img\b[^>]*\bsrc=(["'])([^"']*)\1/gi;
  let match: RegExpExecArray | null;
  while ((match = srcPattern.exec(html)) !== null) {
    if (isRehostableImageSrc(match[2])) {
      return true;
    }
  }
  return false;
}
